<script setup>
import { useMainStore } from '@js/App/Stores/MainStore'
import { computed, defineAsyncComponent, h, watch } from 'vue'
import * as Sentry from '@sentry/vue'
import {
  CREATE_RENTBIRD_PLUS_ACCOUNT_MODAL_ID,
  CREATE_SEARCH_PROFILE_AND_ACCOUNT_MODAL_ID,
  CREATE_RENTBIRD_RELOCATION_ACCOUNT_MODAL_ID,
  VIDEO_MODAL
} from '@js/App/Plugins/Modals'
import BasicModal from '@js/Components/Modals/BasicModal.vue'
import { storeToRefs } from 'pinia'
import ModalVideo from '@js/Components/Modals/ModalVideo/ModalVideo.vue'
import LoadingSpinner from '@js/Components/Forms/LoadingSpinner/LoadingSpinner.vue'
import ErrorMessage from '@js/Components/Forms/ErrorMessage/ErrorMessage.vue'
import { trans } from '@js/App/Plugins/Translations'

// The wizards are only rendered once their modal is opened (v-if below), so their JS
// (incl. the ~74KB PlacesSearch + map deps) is loaded on demand instead of shipping in
// the entry chunk on every page. LoadingSpinner shows while the chunk fetches; a failed
// fetch (classic case: stale chunk hash right after a deploy) is retried once and then
// shown as a visible error and reported, instead of leaving the modal body blank.
const asyncWizard = loader => defineAsyncComponent({
  loader,
  loadingComponent: LoadingSpinner,
  errorComponent: () => h(ErrorMessage, { big: true, class: 'm-6' }, () => trans('Errors.General')),
  onError: (error, retry, fail, attempts) => {
    if (attempts <= 1) {
      retry()
      return
    }

    Sentry.captureException(error)
    fail()
  }
})

const CreateSearchProfileAndAccountWizard = asyncWizard(
  () => import('@js/Components/SearchProfileWizards/SearchProfileWizard/CreateSearchProfileAndAccountWizard.vue')
)
const RentbirdPlusWizard = asyncWizard(
  () => import('@js/Components/SearchProfileWizards/RentbirdPlusWizard/RentbirdPlusWizard.vue')
)
const RentbirdRelocationWizard = asyncWizard(
  () => import('@js/Components/SearchProfileWizards/RentbirdRelocationWizard/RentbirdRelocationWizard.vue')
)
const mainStore = useMainStore()

const showCreateSearchProfileAndAccountModal = computed(() =>
  mainStore.openModals?.includes(CREATE_SEARCH_PROFILE_AND_ACCOUNT_MODAL_ID)
)

const showCreateRentbirdPlusAccountModal = computed(() =>
  mainStore.openModals?.includes(CREATE_RENTBIRD_PLUS_ACCOUNT_MODAL_ID)
)

const showCreateRentbirdRelocationAccountModal = computed(() =>
  mainStore.openModals?.includes(CREATE_RENTBIRD_RELOCATION_ACCOUNT_MODAL_ID)
)

const showVideoModal = computed(() =>
  mainStore.openModals?.includes(VIDEO_MODAL)
)

const isAnyModalOpen = computed(() =>
  mainStore.openModals?.length && mainStore.openModals.length > 0
)

const videoToShowInModal = computed(() => mainStore.videoToShowInModal)

const { fadingOutModals } = storeToRefs(mainStore)

watch(isAnyModalOpen, () => {
  const bodyEl = document.querySelector('body')
  if (isAnyModalOpen.value) {
    bodyEl.classList.remove('overflow-auto')
    bodyEl.classList.add('overflow-hidden')
  } else {
    bodyEl.classList.add('overflow-auto')
    bodyEl.classList.remove('overflow-hidden')
  }
})
</script>
<template>
  <BasicModal
    v-if="showCreateSearchProfileAndAccountModal"
    :show-top-bar="false"
    :padding="false"
    :just-got-closed="fadingOutModals.includes(CREATE_SEARCH_PROFILE_AND_ACCOUNT_MODAL_ID)"
    @close="mainStore.closeModal(CREATE_SEARCH_PROFILE_AND_ACCOUNT_MODAL_ID)"
  >
    <CreateSearchProfileAndAccountWizard />
  </BasicModal>

  <BasicModal
    v-if="showCreateRentbirdPlusAccountModal"
    :show-top-bar="false"
    :padding="false"
    :just-got-closed="fadingOutModals.includes(CREATE_RENTBIRD_PLUS_ACCOUNT_MODAL_ID)"
    @close="mainStore.closeModal(CREATE_RENTBIRD_PLUS_ACCOUNT_MODAL_ID)"
  >
    <RentbirdPlusWizard />
  </BasicModal>

  <BasicModal
    v-if="showCreateRentbirdRelocationAccountModal"
    :show-top-bar="false"
    :padding="false"
    :just-got-closed="fadingOutModals.includes(CREATE_RENTBIRD_RELOCATION_ACCOUNT_MODAL_ID)"
    @close="mainStore.closeModal(CREATE_RENTBIRD_RELOCATION_ACCOUNT_MODAL_ID)"
  >
    <RentbirdRelocationWizard />
  </BasicModal>

  <ModalVideo
    v-if="showVideoModal"
    :video="videoToShowInModal"
    @close="mainStore.closeModal(VIDEO_MODAL)"
  />
</template>
